import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAnnotationStore } from '../annotation/annotationStoreFactory';
import type { Annotation, AnnotationPatch, NewAnnotation } from '../annotation/types';

// Annotations for a study, with optimistic create/update/delete so the overlay
// reflects edits instantly and reconciles with the server in the background.
// Keyed by the study's PUBLIC id: the one name for a study that is not shared
// by a namesake in another workspace.

const key = (study: string | undefined, series?: string) =>
  ['annotations', study, series ?? null] as const;
type ListKey = ReturnType<typeof key>;

/** What a write knows of the list it was issued for: its key, and the list before it. */
interface Issued {
  key: ListKey;
  prev: Annotation[];
}

export function useAnnotations(
  studyId: string | undefined,
  seriesInstanceUID?: string,
  /**
   * A write the server refused, after its optimistic change has been rolled
   * back: the caller says why (a 403 is "you can view this slide but not
   * annotate it"), since otherwise the shape simply vanishes.
   */
  onWriteError?: (error: Error) => void,
) {
  const qc = useQueryClient();
  const queryKey = key(studyId, seriesInstanceUID);
  const store = getAnnotationStore();

  const query = useQuery<Annotation[]>({
    queryKey,
    enabled: !!studyId,
    queryFn: () => store.list(studyId!, seriesInstanceUID),
  });

  // A write settles on the list it was issued for, which is not always the
  // list shown when its answer lands: the slide can change meanwhile (another
  // image, another compare pane), and TanStack hands a pending mutation the
  // latest render's callbacks. So the key is taken as the write goes out and
  // carried in its context, never read from the render its answer lands in.
  const issue = async (): Promise<Issued> => {
    await qc.cancelQueries({ queryKey });
    return { key: queryKey, prev: qc.getQueryData<Annotation[]>(queryKey) ?? [] };
  };
  // Back to the list as it was before the change, then re-read from the server:
  // several writes refused together (Clear all) each restore their own older
  // snapshot, and whichever lands last would otherwise win.
  const rollback = (issued: Issued | undefined) => {
    if (!issued) return;
    qc.setQueryData(issued.key, issued.prev);
    void qc.invalidateQueries({ queryKey: issued.key });
  };

  const create = useMutation({
    mutationFn: (a: NewAnnotation) => store.create(a),
    onMutate: async (a) => {
      const issued = await issue();
      // A counter is drawn by its counting session until it is saved, so it
      // gets no optimistic twin: one would sit over the session's own dots.
      if (a.type === 'counter') return { ...issued, tempId: null };
      const now = new Date().toISOString();
      const optimistic: Annotation = {
        ...a,
        id: `optimistic-${now}-${issued.prev.length}`,
        createdAt: now,
        updatedAt: now,
        // Drawn by the person looking, as the server will say.
        isYou: true,
      };
      qc.setQueryData<Annotation[]>(issued.key, [...issued.prev, optimistic]);
      return { ...issued, tempId: optimistic.id };
    },
    onError: (e, _a, ctx) => {
      // A counter added nothing to take back: restoring the snapshot would only
      // undo whatever else changed while it was saving.
      if (ctx && ctx.tempId === null) void qc.invalidateQueries({ queryKey: ctx.key });
      else rollback(ctx);
      onWriteError?.(e);
    },
    onSuccess: (saved, _a, ctx) => {
      qc.setQueryData<Annotation[]>(ctx?.key ?? queryKey, (list = []) =>
        ctx?.tempId
          ? list.map((x) => (x.id === ctx.tempId ? saved : x))
          : list.some((x) => x.id === saved.id)
            ? list
            : [...list, saved],
      );
    },
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AnnotationPatch }) => store.update(id, patch),
    onMutate: async ({ id, patch }) => {
      const issued = await issue();
      qc.setQueryData<Annotation[]>(
        issued.key,
        issued.prev.map((x) =>
          x.id === id ? { ...x, ...patch, updatedAt: new Date().toISOString() } : x,
        ),
      );
      return issued;
    },
    onError: (e, _v, ctx) => {
      rollback(ctx);
      onWriteError?.(e);
    },
    onSuccess: (saved, _v, ctx) => {
      qc.setQueryData<Annotation[]>(ctx?.key ?? queryKey, (list) =>
        (list ?? []).map((x) => (x.id === saved.id ? saved : x)),
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => store.remove(id),
    onMutate: async (id) => {
      const issued = await issue();
      qc.setQueryData<Annotation[]>(issued.key, issued.prev.filter((x) => x.id !== id));
      return issued;
    },
    onError: (e, _id, ctx) => {
      rollback(ctx);
      onWriteError?.(e);
    },
  });

  return {
    annotations: query.data ?? [],
    isLoading: query.isLoading,
    create,
    update,
    remove,
  };
}
